import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RoleAwareLayout from "../../components/layouts/RoleAwareLayout";
import "./PricingPage.css";

export default function PricingPage() {
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [userType, setUserType] = useState(null); // 'freelancer' or 'business'
  const [locationCount, setLocationCount] = useState(1);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // 'monthly' or 'annual'
  // A code is typed HERE, in OGESTYLE — never on Stripe's payment page (the user was explicit).
  const [promoCode, setPromoCode] = useState("");
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [validatingPromo, setValidatingPromo] = useState(false);
  // Hidden until the server confirms the feature is switched on for this deployment.
  const [promoEnabled, setPromoEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * A code already carried into checkout — the agent's share link, or a code the business typed at
   * signup — is applied automatically, so the link really does offer the discount it promised.
   */
  useEffect(() => {
    if (!promoEnabled) return;
    let stored = null;
    try {
      // The code used at signup, handed over for checkout; falls back to a live arrival code.
      stored =
        localStorage.getItem("ogestyle_promo_code") ||
        localStorage.getItem("ogestyle_referral_code");
    } catch (_err) {
      stored = null;
    }
    if (!stored) return;
    setPromoCode(String(stored).toUpperCase());
    applyPromoCode(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoEnabled]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

      // Fetch subscription status which includes all pricing settings
      const statusRes = await fetch(
        `${apiBaseUrl}/api/subscription-new/status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!statusRes.ok) {
        throw new Error(
          `Failed to fetch subscription status: ${statusRes.status}`,
        );
      }

      const statusData = await statusRes.json();

      setUserType(statusData.type); // 'freelancer' or 'business'
      if (statusData.type === "business") {
        setLocationCount(statusData.locationCount || 1);
      }

      /**
       * Built from the status response, with no fallback numbers (2026-11-01).
       *
       * `annualDiscountPercent || 15` invented a discount the platform never promised, and the page had already
       * been told not to advertise one because the per-professional branch does not apply it. A missing value
       * now renders nothing instead of a made-up figure.
       */
      const settings = {
        subscriptionFreelancerMonthly: statusData.subscriptionAmount,
        subscriptionBasePriceMonthly: statusData.basePriceMonthly,
        subscriptionAdditionalLocationPrice: statusData.additionalLocationPrice,
        annualDiscountPercent: statusData.annualDiscountPercent,
        productFeePercent: statusData.pricing?.productFeePercent,
      };

      setPlatformSettings(settings);

      // A 404 means the plans feature is switched off, so the promo field must not exist at all.
      try {
        const probe = await fetch(
          `${apiBaseUrl}/api/platform-plans/promo/validate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: "" }),
          },
        );
        setPromoEnabled(probe.status !== 404);
      } catch (_err) {
        setPromoEnabled(false);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const calculatePrice = (period = billingPeriod) => {
    if (!platformSettings) return 0;

    let monthlyPrice = 0;

    if (userType === "freelancer") {
      monthlyPrice = platformSettings.subscriptionFreelancerMonthly || 0;
    } else if (userType === "business") {
      const basePrice = platformSettings.subscriptionBasePriceMonthly || 0;
      const additionalPrice =
        platformSettings.subscriptionAdditionalLocationPrice || 0;
      const additionalLocations = Math.max(0, locationCount - 1);
      monthlyPrice = basePrice + additionalPrice * additionalLocations;
    }

    if (period === "annual") {
      // No fallback discount (2026-11-01): a missing setting must not invent a saving the platform does not give.
      const discountPercent = Number(platformSettings.annualDiscountPercent) || 0;
      const discountMultiplier = (100 - discountPercent) / 100;
      return Math.round(monthlyPrice * 12 * discountMultiplier);
    }

    return monthlyPrice;
  };

  /**
   * Check a code before checkout so the business sees the real price, not a surprise on the invoice.
   * The server owns the arithmetic; this only displays what it returns.
   */
  const applyPromoCode = async (codeOverride) => {
    const code = String(codeOverride || promoCode).trim().toUpperCase();
    setPromoError("");
    setPromoPreview(null);
    if (!code) {
      setPromoError("Enter a code.");
      return;
    }

    setValidatingPromo(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-plans/promo/validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            interval: billingPeriod === "annual" ? "year" : "month",
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data.valid) {
        setPromoError(
          response.status === 404
            ? "Promo codes are not available right now."
            : data.message || "That code is not valid.",
        );
        return;
      }
      setPromoPreview(data);
    } catch (_err) {
      setPromoError("Unable to check that code right now.");
    } finally {
      setValidatingPromo(false);
    }
  };
  const handleSubscribe = async (period, promoCode = "") => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

      /**
       * SUBSCRIBE THROUGH THE ROUTE THAT KNOWS ABOUT PROMOS (2026-10-29).
       *
       * This used to POST /api/subscription/create, which has no promo support at all — so a code typed at
       * signup was accepted by the page and silently ignored by Stripe. The new route validates the code and
       * attaches it as a pre-applied coupon. A promoCode is only sent when one was entered, so the no-code
       * path is unchanged.
       */
      const response = await fetch(
        `${apiBaseUrl}/api/subscription-new/create-checkout-session`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            billingPeriod: period,
            ...(promoCode ? { promoCode } : {}),
          }),
        },
      );

      const data = await response.json();

      // The new route returns `url`; still accept `checkoutUrl` so nothing reading this breaks.
      const checkoutUrl = data.url || data.checkoutUrl;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error(
          data.message || data.error || "Failed to create checkout session",
        );
      }
    } catch (err) {
      alert(
        err?.message && err.message !== "Failed to create checkout session"
          ? err.message
          : "Failed to start subscription. Please try again.",
      );
      setSubscribing(false);
    }
  };

  if (loading || !platformSettings) {
    return (
      <RoleAwareLayout>
        <div className="pricing-loading">
          <div className="spinner"></div>
          <p>Loading pricing information...</p>
        </div>
      </RoleAwareLayout>
    );
  }

  const monthlyPrice = calculatePrice("monthly");
  const annualPrice = calculatePrice("annual");
  const annualMonthlyEquivalent = Math.round(annualPrice / 12);
  const savings = monthlyPrice * 12 - annualPrice;

  return (
    <RoleAwareLayout>
      <div className="pricing-page">
        <div className="pricing-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Choose Your Plan</h1>
          <p className="pricing-subtitle">
            Unlock all features and grow your beauty business
          </p>
        </div>

        <div className="billing-toggle">
          <button
            className={billingPeriod === "monthly" ? "active" : ""}
            onClick={() => setBillingPeriod("monthly")}
          >
            Monthly
          </button>
          <button
            className={billingPeriod === "annual" ? "active" : ""}
            onClick={() => setBillingPeriod("annual")}
          >
            Annual
            {Number(platformSettings?.annualDiscountPercent) > 0 && (
              <span className="savings-badge">
                Save {platformSettings.annualDiscountPercent}%
              </span>
            )}
          </button>
        </div>

        {promoEnabled && (
        <div className="promo-code-row">
          <label htmlFor="promo-code">Have a promo code?</label>
          <div className="promo-code-inputs">
            <input
              id="promo-code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Enter your code"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={applyPromoCode}
              disabled={validatingPromo}
            >
              {validatingPromo ? "Checking..." : "Apply"}
            </button>
          </div>
          {promoError && <p className="promo-error">{promoError}</p>}
          {promoPreview && (
            <p className="promo-success">
              {promoPreview.promoCode || promoCode} applied — {promoPreview.percentOff}% off
              {promoPreview.durationMonths
                ? ` for ${promoPreview.durationMonths} months`
                : " with no end date"}.
              {promoPreview.monthlyCents > 0
                ? ` Your first payment is $${(promoPreview.discountedMonthlyCents / 100).toFixed(2)}.`
                : ""}
            </p>
          )}
        </div>
        )}
        <div className="pricing-cards">
          {/* Current Plan Card */}
          <div className="pricing-card featured">
            <div className="card-header">
              <h2>
                {userType === "freelancer" ? "Freelancer" : "Business"} Plan
              </h2>
              <div className="price-display">
                {billingPeriod === "monthly" ? (
                  <>
                    <span className="price">${monthlyPrice}</span>
                    <span className="period">/month</span>
                  </>
                ) : (
                  <>
                    <span className="price">${annualMonthlyEquivalent}</span>
                    <span className="period">/month</span>
                    <div className="annual-note">
                      Billed ${annualPrice} annually
                    </div>
                  </>
                )}
              </div>
              {userType === "business" && locationCount > 1 && (
                <div className="location-info">
                  {locationCount} locations • Base $
                  {platformSettings.subscriptionBasePriceMonthly} +
                  {locationCount - 1} × $
                  {platformSettings.subscriptionAdditionalLocationPrice}
                </div>
              )}
            </div>

            <div className="card-features">
              <h3>Everything You Need:</h3>
              <ul>
                <li>✅ Unlimited online bookings</li>
                <li>
                  ✅ 0% transaction fees on bookings — OGESTYLE takes nothing
                  from your services
                </li>
                {platformSettings?.productFeePercent !== undefined && (
                  <li>
                    ✅ {platformSettings.productFeePercent}% product fee on
                    product sales only
                  </li>
                )}
                <li>✅ Automated appointment reminders</li>
                <li>✅ Client management & history</li>
                <li>✅ Schedule & availability management</li>
                <li>✅ Secure payment processing (Stripe)</li>
                <li>✅ Instant payouts after services</li>
                <li>✅ Marketing email campaigns</li>
                <li>✅ Analytics & earnings reports</li>
                <li>✅ Mobile-friendly booking page</li>
                <li>✅ Priority customer support</li>
                {userType === "business" && (
                  <>
                    <li>✅ Multi-location management</li>
                    <li>✅ Staff management</li>
                    <li>✅ Team scheduling</li>
                  </>
                )}
              </ul>
            </div>

            <button
              className="subscribe-button"
              onClick={() =>
                handleSubscribe(billingPeriod, promoPreview ? promoCode.trim() : "")
              }
              disabled={subscribing}
            >
              {subscribing
                ? "Processing..."
                : `Subscribe ${billingPeriod === "annual" ? "Annually" : "Monthly"}`}
            </button>

            {billingPeriod === "annual" && (
              <div className="savings-highlight">
                💰 You save ${savings.toFixed(2)} per year!
              </div>
            )}
          </div>

          {/* Usage-Based Billing is no longer offered (2026-11-01).
              It was a free ride: the split calculator takes nothing on a service booking, so a
              usage-based shop had full visibility and paid no subscription and no per-booking fee.
              The card that advertised it has been removed rather than left promising a product
              that does not exist. */}
        </div>

        <div className="pricing-footer">
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>
            <div className="faq-item">
              <h4>Can I cancel anytime?</h4>
              <p>
                Yes! You can cancel your subscription anytime from your
                dashboard. Your account will remain active until the end of your
                billing period.
              </p>
            </div>
            <div className="faq-item">
              <h4>What payment methods do you accept?</h4>
              <p>
                We accept all major credit and debit cards through our secure
                Stripe payment processor.
              </p>
            </div>
            <div className="faq-item">
              <h4>Can I add more locations later?</h4>
              <p>
                Yes! Your subscription will automatically adjust based on your
                active locations. Each additional location costs $
                {platformSettings?.subscriptionAdditionalLocationPrice}/month.
              </p>
            </div>
            <div className="faq-item">
              <h4>Is there a contract or commitment?</h4>
              <p>
                No long-term contracts! Pay month-to-month or save with our
                annual plan. Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </div>
    </RoleAwareLayout>
  );
}
